import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, Skeleton } from 'components';
import {
  Container,
  ErrorWarning,
  TimeWindowFormProps,
} from 'components/TimeWindow';
import { CenteredContent, Table } from 'components/Table/TableStyles';
import { Button } from 'components/Button';
import { LagDateBadge } from 'components/LagDateBadge/LagDateBadge';
import { useDefaultLagDate } from 'hooks/useDefaultLagDate';
import { assetSelectionsLoaderFieldName } from './components/TimeWindowHeader';
import { ReturnComponentSearch } from 'components/Select/ReturnComponentSearch';
import { useFormErrors } from 'hooks';

export const BenchmarkAssetWeightedTimeWindow: React.FC<
  TimeWindowFormProps
> = ({ index }) => {
  const {
    control,
    setValue,
    formState: { errors },
    clearErrors,
    watch,
    trigger,
  } = useFormContext();

  const { defaultLagDate } = useDefaultLagDate();
  const fieldName = `timeWindows[${index}]`;
  const { getErrorMessage, hasErrors } = useFormErrors({
    errors,
    fieldName,
    index,
  });
  const lineItemsFieldName = `${fieldName}.lineItems`;

  const { fields, append, remove } = useFieldArray({
    control,
    name: `${fieldName}.lineItems`,
  });
  const watchFieldArray = watch(lineItemsFieldName);
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const isLoading = watch(
    `timeWindows[${index}].${assetSelectionsLoaderFieldName}`
  );

  const selectionHandler = (val: any, lineItemIndex: number) => {
    const newValue = {
      ...controlledFields[lineItemIndex],
      weightingComponent: val.value,
      weightingComponentName: val.label,
    };
    setValue(`${lineItemsFieldName}[${lineItemIndex}]`, newValue, {
      shouldDirty: true,
    });
    trigger(`${lineItemsFieldName}[${lineItemIndex}].weightingComponent`);
  };

  // TODO: return error back

  const handleReturnComponentChange = (val: any, lineItemIndex: number) => {
    const lineItemField = `${lineItemsFieldName}[${lineItemIndex}]`;
    setValue(`${lineItemField}.isLagged`, val.isLagged);
    setValue(`${lineItemField}.customLagDate`, val.customLagDate);
    setValue(`${lineItemField}.returnComponent`, val.value, {
      shouldDirty: true,
    });
    setValue(`${lineItemField}.returnComponentName`, val.label, {
      shouldDirty: true,
    });
  };

  return (
    <Container key={fieldName}>
      <Table>
        <table className='timeWindowTable'>
          <tbody>
            <tr>
              <th style={{ width: '35%' }}>Weighting Component</th>
              <th style={{ width: '35%' }}>Return Component</th>
              <th>Lag Date</th>
              <th className='center'>Actions</th>
            </tr>
            {isLoading ? (
              <Skeleton rows={1} columns={4} />
            ) : (
              <>
                {controlledFields.map((lineItem, lineItemIndex: number) => (
                  <tr key={lineItem.id}>
                    <td>
                      <ReturnComponentSearch
                        size='sm'
                        filter='weighting'
                        isPortalled={true}
                        testid={`${fieldName}.lineItems[${lineItemIndex}].weightingComponent`}
                        onChange={(val) => {
                          selectionHandler(val, lineItemIndex);
                        }}
                        onBlur={() => {
                          trigger(
                            `${fieldName}.lineItems[${lineItemIndex}].weightingComponent`
                          );
                        }}
                        value={
                          lineItem.weightingComponent
                            ? {
                                ...lineItem,
                                value: lineItem.weightingComponent,
                                label: lineItem.weightingComponentName,
                              }
                            : ''
                        }
                        hasError={getErrorMessage(
                          `${fieldName}.lineItems[${lineItemIndex}].weightingComponent`
                        )}
                      />
                    </td>
                    <td>
                      <ReturnComponentSearch
                        size='sm'
                        isPortalled={true}
                        testid={`${fieldName}.lineItems[${lineItemIndex}].returnComponent-search`}
                        onChange={(val) => {
                          handleReturnComponentChange(val, lineItemIndex);
                          trigger(
                            `${fieldName}.lineItems[${lineItemIndex}].returnComponent`
                          );
                        }}
                        onBlur={() => {
                          trigger(
                            `${fieldName}.lineItems[${lineItemIndex}].returnComponent`
                          );
                        }}
                        value={
                          lineItem.returnComponent
                            ? {
                                ...lineItem,
                                value: lineItem.returnComponent,
                                label: lineItem.returnComponentName,
                              }
                            : ''
                        }
                        hasError={getErrorMessage(
                          `${fieldName}.lineItems[${lineItemIndex}].returnComponent`
                        )}
                      />
                    </td>
                    <td className='lagDate'>
                      {LagDateBadge({
                        isLagged: lineItem.isLagged,
                        customLagDate: lineItem.customLagDate,
                        defaultLagDate: defaultLagDate,
                      })}
                    </td>
                    <td>
                      <CenteredContent>
                        <Button
                          variant='tertiary'
                          style={{ color: '#F8333C', fontSize: '12px' }}
                          onClick={() => {
                            remove(lineItemIndex);
                          }}
                          className='table'
                        >
                          Remove
                        </Button>
                      </CenteredContent>
                    </td>
                  </tr>
                ))}
                <tr key='add component'>
                  <td align='left' colSpan={4}>
                    <Button
                      data-testid={`add-component-${index}`}
                      style={{ height: '24px' }}
                      variant='secondary'
                      onClick={() => {
                        clearErrors(`${fieldName}.lineItems`);
                        append({
                          returnComponent: '',
                          weightingComponent: '',
                          isLagged: false,
                          customLagDate: null,
                          lagDateInfo: null,
                        });
                      }}
                    >
                      <Plus size='10px' />
                      Component
                    </Button>
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </Table>
      <ErrorWarning
        hasErrors={hasErrors && fields.length < 1}
        errorMessage='This field is required'
      />
    </Container>
  );
};
